import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Lock } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Alert, AlertDescription } from 'components/ui/alert';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import DataTable from 'components/ui/data-table';
import LoadingState from 'components/shared/LoadingState';
import { getMyBlogPosts, deleteBlogPost } from '../../services/api';

const STATUS_BADGE_VARIANT = {
  draft: 'outline',
  pending_review: 'warning',
  published: 'success',
  archived: 'destructive',
};

const STATUS_LABELS = {
  all: 'All',
  draft: 'Draft',
  pending_review: 'Pending review',
  published: 'Published',
  archived: 'Archived',
};

const STATUS_FILTERS = ['all', 'draft', 'pending_review', 'published'];

// The backend paginates /creator/getMyBlogPosts (default page_size 20, max 100).
// The list view, status pills and sorting are all client-side, so fetch every
// page up front. Post rows are metadata-only (no body content), and creators
// only see their own posts, so this stays cheap.
const fetchAllBlogPosts = async () => {
  const pageSize = 100;
  const first = await getMyBlogPosts({ page: 1, page_size: pageSize });
  const posts = [...(first.posts || [])];
  const totalPages = first.total_pages || 1;
  for (let page = 2; page <= Math.min(totalPages, 50); page += 1) {
    const res = await getMyBlogPosts({ page, page_size: pageSize });
    posts.push(...(res.posts || []));
  }
  return posts;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Blog Editor Component (creator: own posts only)
// ─────────────────────────────────────────────────────────────────────────────

export default function BlogEditor() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Delete confirmation (replaces window.confirm)
  const [deleteTarget, setDeleteTarget] = useState(null); // post id | null
  const [deleting, setDeleting] = useState(false);

  // Fetch data on mount
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setPosts(await fetchAllBlogPosts());
    } catch (err) {
      setError(err.message || 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePost = () => {
    navigate('/blog/new');
  };

  const handleEditPost = useCallback(
    (post) => {
      navigate(`/blog/edit/${post.id}`);
    },
    [navigate]
  );

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteBlogPost(deleteTarget);
      setPosts(await fetchAllBlogPosts());
      toast.success('Post deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete post');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const filteredPosts = useMemo(
    () =>
      statusFilter === 'all'
        ? posts
        : posts.filter((p) => p.status === statusFilter),
    [posts, statusFilter]
  );

  const statusCounts = useMemo(() => {
    const counts = { all: posts.length };
    posts.forEach((p) => {
      counts[p.status] = (counts[p.status] || 0) + 1;
    });
    return counts;
  }, [posts]);

  const postColumns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate text-token-md font-medium">{row.original.title}</p>
            <p className="truncate text-muted-foreground">/{row.original.slug}</p>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={STATUS_BADGE_VARIANT[row.original.status] || 'outline'}>
              {STATUS_LABELS[row.original.status] || row.original.status}
            </Badge>
            {row.original.is_premium && <Badge variant="info">Premium</Badge>}
          </div>
        ),
      },
      {
        accessorKey: 'category_name',
        header: 'Category',
        cell: ({ row }) => row.original.category_name || '–',
      },
      {
        id: 'season_gw',
        header: 'Season/GW',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.season && row.original.gameweek
            ? `${row.original.season} GW${row.original.gameweek}`
            : row.original.season || '–',
      },
      {
        accessorKey: 'published_at',
        header: 'Published',
        cell: ({ row }) =>
          row.original.published_at
            ? new Date(row.original.published_at).toLocaleDateString()
            : '–',
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { cellClassName: 'text-right whitespace-nowrap' },
        cell: ({ row }) =>
          row.original.status === 'published' ? (
            // Published posts are read-only for creators
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex p-2 text-muted-foreground">
                  <Lock className="h-4 w-4" aria-label="Locked" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">
                Published posts are managed by DraftFC
              </TooltipContent>
            </Tooltip>
          ) : (
            <div className="inline-flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleEditPost(row.original)}
                title="Edit"
              >
                <Pencil />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteTarget(row.original.id)}
                title="Delete"
              >
                <Trash2 />
              </Button>
            </div>
          ),
      },
    ],
    [handleEditPost]
  );

  if (loading) {
    return <LoadingState message="Loading blog posts..." />;
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={fetchData}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div>
        {/* Header */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-token-xl font-semibold">
            My Blog Posts ({posts.length})
          </h1>
          <Button size="sm" onClick={handleCreatePost}>
            <Plus /> New Post
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-3">
          {/* Status filter pills — client-side over the already-fetched list */}
          <ToggleGroup
            type="single"
            value={statusFilter}
            onValueChange={(v) => v && setStatusFilter(v)}
            className="self-start"
          >
            {STATUS_FILTERS.map((s) => (
              <ToggleGroupItem key={s} value={s} size="sm">
                {STATUS_LABELS[s]}
                {statusCounts[s] ? ` (${statusCounts[s]})` : s === 'all' ? ' (0)' : ''}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <DataTable
            columns={postColumns}
            data={filteredPosts}
            emptyMessage={
              statusFilter === 'all'
                ? 'No posts yet. Create your first blog post!'
                : `No ${(STATUS_LABELS[statusFilter] || statusFilter).toLowerCase()} posts.`
            }
          />
        </div>

        {/* Delete confirmation (replaces window.confirm) */}
        <Dialog
          open={Boolean(deleteTarget)}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
        >
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader>
              <DialogTitle>Delete this post?</DialogTitle>
            </DialogHeader>
            <p className="text-token-md text-muted-foreground">
              The post will be removed from your blog posts. This cannot be
              undone.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting && <Loader2 className="animate-spin" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
